import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { NOINDEX_PRIVATE_SEO } from "../../../constants/siteSeo";
import { usePageSeo } from "../../../hooks/usePageSeo";
import "../marketplace.css";
import {
  fetchMyMarketplacePurchases,
  marketplaceBuyerRequiresLogin,
} from "../../../services/marketplaceBuyerApi";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return "—";
  }
}

function formatUsdCents(cents) {
  const n = Math.max(0, Number(cents) || 0);
  return `$${(n / 100).toFixed(2)}`;
}

function productTitle(ent) {
  const p = ent.productId;
  if (p && typeof p === "object" && p.title) return String(p.title);
  return "—";
}

function storeTitle(ent) {
  const s = ent.storeId;
  if (s && typeof s === "object" && s.storeName) return String(s.storeName);
  return "—";
}

function paymentMethodLabel(m) {
  const x = String(m || "").toLowerCase();
  if (x === "card") return "Card";
  if (x === "treydorex") return "Treydorex";
  return x ? x : "—";
}

function paymentStatusFromOrder(order) {
  if (!order || typeof order !== "object") return "—";
  return String(order.status || "—");
}

function pricePaidDisplay(ent) {
  const ord = ent.orderId;
  if (ord && typeof ord === "object") {
    const pm = String(ord.paymentMethod || "").toLowerCase();
    if (pm === "treydorex") {
      const g = String(ord.grossTrdxAmount || "").trim();
      return g ? `${g} TRDX` : "TRDX (amount pending)";
    }
    if (Number(ord.grossAmountCents) > 0) return formatUsdCents(ord.grossAmountCents);
  }
  const p = ent.productId;
  if (p && typeof p === "object" && Number(p.priceCents) > 0) {
    return `${formatUsdCents(p.priceCents)} (list)`;
  }
  return "—";
}

function accessLabel(status) {
  const s = String(status || "").toLowerCase();
  if (s === "active") return "Active";
  if (s === "revoked") return "Revoked";
  if (s === "refunded") return "Refunded";
  return s || "—";
}

export default function MarketplacePurchasesPage() {
  usePageSeo({
    ...NOINDEX_PRIVATE_SEO,
    title: "Marketplace purchases | Treydora",
    description: "Your marketplace order history — not indexed.",
  });
  const [searchParams] = useSearchParams();
  const checkoutFlag = String(searchParams.get("marketplaceCheckout") || "").toLowerCase();
  const checkoutOrderId = String(searchParams.get("orderId") || "").trim();

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [rows, setRows] = useState([]);

  const load = useCallback(async () => {
    if (marketplaceBuyerRequiresLogin()) {
      setLoading(false);
      setRows([]);
      setErr("");
      return;
    }
    setLoading(true);
    setErr("");
    try {
      const list = await fetchMyMarketplacePurchases();
      setRows(Array.isArray(list) ? list : []);
    } catch (e) {
      setErr(e?.message || "Could not load purchases.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (marketplaceBuyerRequiresLogin()) {
    return (
      <div className="mp-page mp-page--dash">
        <div className="mp-hero">
          <div className="mp-hero__glow" aria-hidden />
          <p className="mp-hero__eyebrow">Treydora Marketplace</p>
          <h1 className="mp-hero__title">Marketplace purchases</h1>
          <p className="mp-hero__sub">Log in to see digital goods you have bought from Oshar storefronts.</p>
          <Link to="/marketplace" className="mp-link">
            ← Browse marketplace
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="mp-page mp-page--dash">
      <div className="mp-hero">
        <div className="mp-hero__glow" aria-hidden />
        <p className="mp-hero__eyebrow">Treydora Marketplace</p>
        <h1 className="mp-hero__title">Marketplace purchases</h1>
        <p className="mp-hero__sub">
          Paid marketplace card orders appear here after Stripe confirms payment. Downloads stay disabled until secure
          delivery ships.
        </p>
        <Link to="/marketplace" className="mp-link">
          ← Browse marketplace
        </Link>
      </div>

      {checkoutFlag === "success" ? (
        <div className="mp-banner mp-banner--success" role="status">
          Purchase received. Your marketplace item will appear below once payment confirmation completes
          {checkoutOrderId ? " (order pending sync)." : "."}
        </div>
      ) : null}
      {checkoutFlag === "cancelled" ? (
        <div className="mp-banner mp-banner--warn" role="status">
          Checkout was cancelled before payment.
        </div>
      ) : null}
      {err ? (
        <div className="mp-banner mp-banner--warn" role="alert">
          {err}
          <div style={{ marginTop: "0.75rem" }}>
            <button type="button" className="mp-btn mp-btn--enabled mp-btn--primary" onClick={() => load()}>
              Retry
            </button>
          </div>
        </div>
      ) : null}

      {loading ? (
        <p className="mp-muted">Loading purchases…</p>
      ) : !err && rows.length === 0 ? (
        <div className="mp-empty-state">
          <h2 className="mp-empty-state__title">No marketplace purchases yet.</h2>
          <p className="mp-muted">
            After a successful card purchase, your entitlement will list here. If you just returned from Stripe, wait
            a moment and use Retry.
          </p>
        </div>
      ) : !err && rows.length > 0 ? (
        <>
          <p className="mp-muted mp-dash-meta">
            {rows.length} purchase{rows.length !== 1 ? "s" : ""} (newest first)
          </p>

          <div className="mp-table-wrap mp-dash-table">
            <table className="mp-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Product</th>
                  <th>Store</th>
                  <th>Price paid</th>
                  <th>Payment method</th>
                  <th>Payment status</th>
                  <th>Access</th>
                  <th>Downloads</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((e) => (
                  <tr key={e._id}>
                    <td>{formatDate(e.createdAt)}</td>
                    <td>{productTitle(e)}</td>
                    <td>{storeTitle(e)}</td>
                    <td>{pricePaidDisplay(e)}</td>
                    <td>{paymentMethodLabel(e.orderId && e.orderId.paymentMethod)}</td>
                    <td>{paymentStatusFromOrder(e.orderId)}</td>
                    <td>
                      <span className="mp-status-chip">{accessLabel(e.accessStatus)}</span>
                    </td>
                    <td>
                      <button type="button" className="mp-btn mp-btn--dl" disabled title="Not enabled">
                        Downloads are not enabled yet.
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mp-dash-cards" aria-label="Purchase cards (mobile)">
            {rows.map((e) => (
              <article key={`pc-${e._id}`} className="mp-dash-card">
                <header className="mp-dash-card__head">
                  <span className="mp-status-chip">{accessLabel(e.accessStatus)}</span>
                  <time className="mp-dash-card__time">{formatDate(e.createdAt)}</time>
                </header>
                <h3 className="mp-dash-card__title">{productTitle(e)}</h3>
                <p className="mp-muted mp-dash-card__store">{storeTitle(e)}</p>
                <dl className="mp-dash-dl">
                  <div>
                    <dt>Price paid</dt>
                    <dd>{pricePaidDisplay(e)}</dd>
                  </div>
                  <div>
                    <dt>Payment method</dt>
                    <dd>{paymentMethodLabel(e.orderId && e.orderId.paymentMethod)}</dd>
                  </div>
                  <div>
                    <dt>Payment status</dt>
                    <dd>{paymentStatusFromOrder(e.orderId)}</dd>
                  </div>
                  <div>
                    <dt>Entitlement</dt>
                    <dd>{accessLabel(e.accessStatus)}</dd>
                  </div>
                </dl>
                <div className="mp-dash-card__actions">
                  <button type="button" className="mp-btn mp-btn--dl" disabled title="Not enabled">
                    Downloads are not enabled yet.
                  </button>
                </div>
              </article>
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}
